/**
 * Quasar's `$q.platform.is.mobile` (legacy back office, quasar/src/plugins/platform/Platform.js), which legacy's fields
 * read to choose between desktop and mobile behaviour. It is a user-agent test, not a pointer test: the first platform
 * the agent names decides (iPad, iPod, Windows Phone, iPhone, Kindle, Silk, Android, then the desktop systems, then
 * PlayBook and BlackBerry), and any agent containing "mobile" counts too. Two client corrections follow it: a touch Mac
 * running desktop Safari is an iPad asking for the desktop site, and `navigator.userAgentData.mobile` overrides a
 * desktop answer.
 */
const PLATFORMS = ['ipad', 'ipod', 'windows phone', 'iphone', 'kindle', 'silk', 'android', 'win', 'mac', 'linux', 'cros', 'playbook', 'bb', 'blackberry'] as const;
const MOBILE_PLATFORMS: ReadonlySet<string> = new Set(['ipad', 'ipod', 'windows phone', 'iphone', 'kindle', 'silk', 'android', 'playbook', 'bb', 'blackberry']);
/**
 * Quasar's getMatch, in its order and with its patterns: the first that matches names the browser (group 5, else 3, else 1). Only
 * "safari" matters here, so the list stops after the two Safari patterns; Firefox and the rest come later in Quasar's
 * list and so never shadow Safari.
 */
const BROWSER_MATCHES: readonly RegExp[] = [
  /(edg|edge|edga|edgios)\/([\w.]+)/,
  /(opr)[/]([\w.]+)/,
  /(vivaldi)[/]([\w.]+)/,
  /(chrome|crios)[/]([\w.]+)/,
  /(version)(applewebkit)[/]([\w.]+).*(safari)[/]([\w.]+)/,
  /(webkit)[/]([\w.]+).*(version)[/]([\w.]+).*(safari)[/]([\w.]+)/,
];

function browserName(agent: string): string {
  for (const pattern of BROWSER_MATCHES) {
    const match = pattern.exec(agent);
    if (match !== null) return match[5] || match[3] || match[1] || '';
  }
  return '';
}

interface BrowserTraits {
  readonly userAgent: string;
  /** `'ontouchstart' in window || navigator.maxTouchPoints > 0`, as Quasar's hasTouch. */
  readonly hasTouch: boolean;
  /** `navigator.userAgentData?.mobile`, where the browser has it. */
  readonly userAgentDataMobile?: boolean | undefined;
}

export function isMobileBrowser({ userAgent, hasTouch, userAgentDataMobile }: BrowserTraits): boolean {
  const agent = userAgent.toLowerCase();
  const platform = PLATFORMS.find((name) => agent.includes(name));
  if ((platform !== undefined && MOBILE_PLATFORMS.has(platform)) || agent.includes('mobile')) return true;
  if (hasTouch && platform === 'mac' && browserName(agent) === 'safari') return true;
  return userAgentDataMobile === true;
}

/** This browser's traits, read when asked (tests stub navigator). */
function currentBrowser(): BrowserTraits {
  const nav = window.navigator as Navigator & { readonly userAgentData?: { readonly mobile?: boolean } };
  return { userAgent: nav.userAgent, hasTouch: 'ontouchstart' in window || nav.maxTouchPoints > 0, userAgentDataMobile: nav.userAgentData?.mobile };
}

/**
 * Where focus goes once a clearable field's clear icon has emptied it, as Quasar's clearValue (use-field.js 325-335): a
 * desktop browser focuses the field (`target`); a mobile one blurs whatever of the field still holds focus (the field
 * itself, or the icon a tap focused), so the keyboard doesn't open.
 */
export function settleFocusAfterClear(target: HTMLElement | null, button: HTMLElement, browser: BrowserTraits = currentBrowser()): void {
  if (!isMobileBrowser(browser)) {
    target?.focus();
    return;
  }
  const active = document.activeElement;
  if (active instanceof HTMLElement && (active === button || (target?.contains(active) ?? false))) active.blur();
}
